import React, { useEffect, useMemo, useState } from 'react';
import { logisticsService } from '../services/api';
import MultiplePaymentEvidenceUpload from './MultiplePaymentEvidenceUpload';
import * as Icons from 'lucide-react';
import toast from 'react-hot-toast';

export default function ChangeShippingModal({ order, onClose, onSuccess }) {
    const [carriers, setCarriers] = useState([]);
    const [deliveryMethods, setDeliveryMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const [deliveryMethod, setDeliveryMethod] = useState(order?.delivery_method || '');
    const [carrierId, setCarrierId] = useState(order?.carrier_id ? String(order.carrier_id) : '');
    const [trackingNumber, setTrackingNumber] = useState(order?.tracking_number || '');
    const [reason, setReason] = useState('');
    const [paymentMethod, setPaymentMethod] = useState(order?.payment_method || '');
    const [generateGuideNow, setGenerateGuideNow] = useState(true);
    const [evidenceCount, setEvidenceCount] = useState(0);
    // Initialize with order's delivery date if exists, formatted as YYYY-MM-DD (timezone-safe)
    const [deliveryDate, setDeliveryDate] = useState(() => {
        if (!order?.delivery_date) return '';
        const d = new Date(order.delivery_date);
        // Adjust for timezone offset to avoid date shifting (same pattern as table column)
        const adjusted = new Date(d.valueOf() + d.getTimezoneOffset() * 60000);
        const yyyy = adjusted.getFullYear();
        const mm = String(adjusted.getMonth() + 1).padStart(2, '0');
        const dd = String(adjusted.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    });

    const isLocalDelivery = useMemo(() => {
        const method = String(deliveryMethod || '').toLowerCase();
        return method.includes('domicilio') || method.includes('local') || method.includes('ciudad');
    }, [deliveryMethod]);

    const isPickup = useMemo(() => {
        const method = String(deliveryMethod || '').toLowerCase();
        return method.includes('recoge') || method.includes('bodega') || method.includes('tienda');
    }, [deliveryMethod]);

    const needsCarrier = useMemo(() => {
        if (isPickup) return false;
        // Si es local, se asigna automáticamente carrier 32 en el backend, 
        // pero podemos dejarlo visible para confirmación.
        return true;
    }, [isPickup]);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setLoading(true);
                const [carriersList, methodsResponse] = await Promise.all([
                    logisticsService.getCarriers(),
                    logisticsService.getDeliveryMethodsActive().catch(err => {
                        console.warn('Error fetching delivery methods:', err);
                        return { success: false, data: [] };
                    })
                ]);

                if (!mounted) return;

                setCarriers(Array.isArray(carriersList) ? carriersList : []);

                // Cargar métodos de envío desde API y mapear a formato de select
                let methods = [];
                try {
                    // Normalizar respuesta: puede ser el array directo, o un objeto con .data
                    // La API /active suele devolver el array en .data, pero el servicio api.js devuleve data directamente
                    const rawData = methodsResponse.data || methodsResponse;

                    if (Array.isArray(rawData)) {
                        methods = rawData.map(m => ({
                            value: m.code, // Usar 'code' como value (ej: 'recoge_bodega')
                            label: m.name  // Usar 'name' como label (ej: 'Recoge en Bodega')
                        }));
                    }
                } catch (err) {
                    console.warn('Error parsing delivery methods response', err);
                }
                // Fallback de seguridad (Actualizado a valores reales de BD)
                setDeliveryMethods([
                    { value: 'recoge_bodega', label: 'Recoge en Bodega' },
                    { value: 'recoge_posterior', label: 'Recoge Posterior' },
                    { value: 'domicilio', label: 'Domicilio local' },
                    { value: 'nacional', label: 'Nacional' },
                    { value: 'mensajeria_urbana', label: 'Mensajería Externa' }
                ]);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, []);

    // Efecto para auto-seleccionar transportadora si es local
    // Solo auto-selecciona si el pedido NO tenía carrier previamente asignado
    useEffect(() => {
        const orderHadCarrier = order?.carrier_id;
        if (isLocalDelivery && !carrierId && !orderHadCarrier && carriers.length > 0) {
            const localCarrier = carriers.find(c => c.id === 32 || String(c.name).toLowerCase().includes('mensajería local'));
            if (localCarrier) {
                setCarrierId(String(localCarrier.id));
            }
        }
    }, [isLocalDelivery, carriers, carrierId, order?.carrier_id]);

    const handleSubmit = async () => {
        if (!deliveryMethod) {
            toast.error('Selecciona un método de envío');
            return;
        }

        // Validación: Transferencia requiere comprobante
        if (paymentMethod === 'transferencia' && evidenceCount === 0) {
            toast.error('Es obligatorio subir el comprobante para Transferencia Bancaria');
            return;
        }

        const needsCarrierInput = needsCarrier && !isLocalDelivery;

        if (needsCarrierInput && !carrierId) {
            toast.error('Selecciona una transportadora');
            return;
        }

        if (!reason || reason.trim().length < 3) {
            toast.error('Ingresa un motivo (mín. 3 caracteres)');
            return;
        }

        try {
            setSubmitting(true);

            const payload = {
                delivery_method: deliveryMethod,
                carrier_id: carrierId ? Number(carrierId) : null,
                tracking_number: trackingNumber || null,
                reason: reason.trim(),
                payment_method: paymentMethod,
                delivery_date: deliveryDate || null
            };

            await logisticsService.updateDeliveryMethod(order.id, payload);

            toast.success('Información de envío actualizada');

            if (typeof onSuccess === 'function') {
                onSuccess({
                    orderId: order.id,
                    newCarrierId: payload.carrier_id,
                    newDeliveryMethod: payload.delivery_method,
                    newDeliveryDate: payload.delivery_date,
                    generateGuide: generateGuideNow
                });
            }
            if (typeof onClose === 'function') onClose();
        } catch (e) {
            const msg = e?.response?.data?.message || 'No se pudo actualizar el envío';
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="bg-gray-50 px-6 py-4 border-b flex items-center justify-between shrink-0">
                    <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                        <Icons.Truck className="w-6 h-6 text-blue-600" />
                        Modificar Envío
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                    >
                        <Icons.X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto grow">
                    {/* Alerta de reseteo (Full Width) */}
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6 flex items-start gap-3">
                        <Icons.Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-semibold text-blue-900">
                                Pedido: <span className="font-bold">{order.order_number}</span>
                            </p>
                            <p className="text-xs text-blue-700 mt-1">
                                Esta acción reseteará cualquier guía de envío generada anteriormente y requerirá una nueva confirmación logística.
                            </p>
                        </div>
                    </div>

                    {/* Mensajero asignado (solo lectura) */}
                    {(order?.assigned_messenger_name || order?.messenger_name || order?.assigned_messenger_id) && (
                        <div className="bg-green-50 border border-green-100 rounded-lg px-4 py-2.5 mb-2 flex items-center gap-2">
                            <Icons.Bike className="w-4 h-4 text-green-600 shrink-0" />
                            <span className="text-sm text-green-800">
                                <span className="font-semibold">Mensajero:</span>{' '}
                                {order?.assigned_messenger_name || order?.messenger_name || `ID: ${order.assigned_messenger_id}`}
                            </span>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* COLUMNA IZQUIERDA: PAGO */}
                        <div className="space-y-4">
                            <h4 className="text-sm font-bold text-gray-900 uppercase border-b pb-2 mb-3">
                                Información de Pago
                            </h4>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Método de Pago
                                </label>
                                <select
                                    value={paymentMethod}
                                    onChange={(e) => setPaymentMethod(e.target.value)}
                                    className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 py-2.5 px-3"
                                >
                                    <option value="">Seleccione un método...</option>
                                    <option value="efectivo">Efectivo</option>
                                    <option value="transferencia">Transferencia Bancaria</option>
                                    <option value="cliente_credito">Cliente a Crédito</option>
                                    <option value="pago_electronico">Pago Electrónico</option>
                                    <option value="mercado_pago">Mercado Pago</option>
                                    <option value="contraentrega">Contraentrega (Solo Bogotá)</option>
                                    <option value="publicidad">Publicidad (sin validación)</option>
                                    <option value="reposicion">Reposición (sin validación)</option>
                                </select>
                                <p className="text-[11px] text-gray-500 mt-1.5 flex items-center">
                                    <Icons.AlertCircle className="w-3 h-3 mr-1" />
                                    Si cambia a envío nacional, use Transferencia.
                                </p>
                            </div>

                            {/* Subida de Comprobantes */}
                            {paymentMethod === 'transferencia' && (
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 animate-in fade-in slide-in-from-top-2">
                                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center text-xs uppercase">
                                        <Icons.Upload className="w-3.5 h-3.5 mr-1.5" />
                                        Comprobantes (Ctrl+V)
                                    </h4>
                                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                                        <MultiplePaymentEvidenceUpload
                                            orderId={order.id}
                                            onEvidencesChange={(count) => setEvidenceCount(count)}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* COLUMNA DERECHA: LOGÍSTICA */}
                        <div className="space-y-4">
                            <h4 className="text-sm font-bold text-gray-900 uppercase border-b pb-2 mb-3">
                                Detalles de Envío
                            </h4>

                            {/* Método de Envío */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Método de Envío
                                </label>
                                {loading ? (
                                    <div className="h-10 bg-gray-100 animate-pulse rounded-lg border"></div>
                                ) : (
                                    <select
                                        value={deliveryMethod}
                                        onChange={(e) => {
                                            setDeliveryMethod(e.target.value);
                                            if (e.target.value.includes('recoge')) {
                                                setCarrierId('');
                                                setTrackingNumber('');
                                            }
                                        }}
                                        className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 py-2.5 px-3"
                                    >
                                        <option value="">Seleccione un método...</option>
                                        {deliveryMethods.map(m => (
                                            <option key={m.value} value={m.value}>{m.label}</option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            {/* Fecha de Entrega */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Fecha de Entrega
                                </label>
                                <input
                                    type="date"
                                    value={deliveryDate}
                                    onChange={(e) => setDeliveryDate(e.target.value)}
                                    className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 py-2.5 px-3"
                                />
                            </div>

                            {/* Transportadora */}
                            {!isPickup && (
                                <div className="animate-in fade-in slide-in-from-top-1">
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                                        Transportadora
                                    </label>
                                    {loading ? (
                                        <div className="h-10 bg-gray-100 animate-pulse rounded-lg border"></div>
                                    ) : (
                                        <select
                                            value={carrierId}
                                            onChange={(e) => setCarrierId(e.target.value)}
                                            className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 py-2.5 px-3"
                                            disabled={isLocalDelivery && carrierId === '32'}
                                        >
                                            <option value="">Seleccione una transportadora...</option>
                                            {carriers.map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                            )}

                            {/* Guía/Tracking */}
                            {!isPickup && (
                                <div className="animate-in fade-in slide-in-from-top-1">
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                                        Número de Guía (Opcional)
                                    </label>
                                    <input
                                        type="text"
                                        value={trackingNumber}
                                        onChange={(e) => setTrackingNumber(e.target.value)}
                                        placeholder="Ingrese guía si ya la tiene"
                                        className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 py-2.5 px-3"
                                    />
                                </div>
                            )}

                            {/* Motivo */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Motivo del Cambio
                                </label>
                                <textarea
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="Explique brevemente la razón del cambio..."
                                    className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 p-3 text-sm"
                                    rows={3}
                                />
                            </div>

                            {/* Toggle Regenerar */}
                            <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 border border-gray-200">
                                <div className="text-sm">
                                    <p className="font-semibold text-gray-800">Regenerar guía</p>
                                    <p className="text-xs text-gray-500 mt-0.5">Anular anterior y generar nueva</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        checked={generateGuideNow}
                                        onChange={(e) => setGenerateGuideNow(e.target.checked)}
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-4 border-t flex justify-end gap-3 shrink-0">
                    <button
                        onClick={onClose}
                        className="px-5 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                        disabled={submitting}
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold shadow-sm transition-colors flex items-center gap-2 disabled:bg-blue-400"
                        disabled={submitting || loading}
                    >
                        {submitting ? (
                            <Icons.Loader className="w-4 h-4 animate-spin" />
                        ) : (
                            <Icons.Save className="w-4 h-4" />
                        )}
                        {submitting ? 'Guardando...' : 'Confirmar Cambios'}
                    </button>
                </div>
            </div>
        </div>
    );
}
